import { useState, useEffect } from "react";
import {
  getAdminBanner,
  updateAdminBanner,
  type AdminBannerConfig,
} from "../../services/adminService";

export function AdminBannerPanel() {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [type, setType] = useState<AdminBannerConfig["type"]>("info");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!expanded) return;
    setLoading(true);
    setError(null);

    getAdminBanner()
      .then((config) => {
        setEnabled(config.enabled);
        setType(config.type);
        setMessage(config.message);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load banner config");
      })
      .finally(() => setLoading(false));
  }, [expanded]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await updateAdminBanner({ enabled, type, message });
      setSuccess("Banner updated successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update banner");
    } finally {
      setSaving(false);
    }
  };

  const typeOptions: { value: AdminBannerConfig["type"]; label: string; color: string }[] = [
    { value: "info", label: "Info", color: "cyan" },
    { value: "warning", label: "Warning", color: "amber" },
    { value: "critical", label: "Critical", color: "red" },
  ];

  return (
    <div className="rounded-xl bg-surface-800/50 border border-surface-700/50 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-surface-700/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-orange-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
              />
            </svg>
          </div>
          <div className="text-left">
            <div className="text-sm font-medium text-surface-100">
              Site Banner
            </div>
            <div className="text-xs text-surface-400">
              Display a custom announcement banner to all users
            </div>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-surface-500 transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {expanded && (
        <div className="border-t border-surface-700/50 p-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-4">
              <div className="w-5 h-5 border-2 border-surface-600 border-t-cyan-400 rounded-full animate-spin" />
            </div>
          )}

          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
              {success}
            </div>
          )}

          {!loading && (
            <>
              {/* Enable Toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm text-surface-200">
                  Banner Enabled
                </label>
                <button
                  type="button"
                  onClick={() => setEnabled(!enabled)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    enabled
                      ? "bg-cyan-500"
                      : "bg-surface-600"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                      enabled ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {/* Banner Type */}
              <div className="space-y-2">
                <label className="text-sm text-surface-200">Banner Type</label>
                <div className="flex gap-2">
                  {typeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setType(opt.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                        type === opt.value
                          ? opt.color === "cyan"
                            ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/50"
                            : opt.color === "amber"
                              ? "bg-amber-500/20 text-amber-400 border border-amber-500/50"
                              : "bg-red-500/20 text-red-400 border border-red-500/50"
                          : "bg-surface-700/50 text-surface-400 border border-surface-600 hover:bg-surface-600/50"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Banner Message */}
              <div className="space-y-2">
                <label className="text-sm text-surface-200">
                  Banner Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Enter the message to display in the banner..."
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg bg-surface-900/50 border border-surface-700 text-surface-100 text-sm placeholder:text-surface-500 focus:outline-none focus:border-cyan-500/50 resize-none"
                />
              </div>

              {/* Preview */}
              {message && (
                <div className="space-y-2">
                  <label className="text-xs text-surface-400 uppercase tracking-wider">
                    Preview
                  </label>
                  <div
                    className={`rounded-lg px-4 py-2.5 text-xs sm:text-sm ${
                      type === "info"
                        ? "bg-cyan-500/10 border border-cyan-500/30 text-cyan-200"
                        : type === "warning"
                          ? "bg-amber-500/10 border border-amber-500/30 text-amber-200"
                          : "bg-red-500/10 border border-red-500/30 text-red-200"
                    }`}
                  >
                    {message}
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-cyan-500/20 text-cyan-400 border border-cyan-500/50 hover:bg-cyan-500/30 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Banner"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
